import { GoogleOAuthProvider } from '@react-oauth/google';
import { HelmetProvider } from 'react-helmet-async';
import { useEffect } from "react";
import {
    Navigate,
    Route,
    BrowserRouter as Router,
    Routes,
    useLocation,
    useNavigate,
} from "react-router-dom";

// Import components
import Analytics from "./components/analytics/Analytics";
import OptionAnalytics from "./components/analytics/OptionAnalytics";
import LoginForm from "./components/auth/LoginForm";
import OAuthCallback from "./components/auth/OAuthCallback";
import ResetPassword from "./components/auth/ResetPassword";
import PositionSizer from "./components/tools/PositionSizer";
import ImportWizard from "./components/tools/ImportWizard";
import Brokers from "./components/brokers/Brokers";
import Dashboard from "./components/dashboard/Dashboard";
import Journal from "./components/journal/Journal";
import Layout from "./components/layout/Layout";
import Playbooks from "./components/playbook/Playbooks";
import Positions from "./components/positions/Positions";
import Trades from "./components/trades/Trades";
import ErrorBoundary from "./components/ErrorBoundary";
import Strategy from "./components/Strategy";
import PrivacyPolicy from "./components/legal/PrivacyPolicy";
import TermsOfService from "./components/legal/TermsOfService";
import Disclaimer from "./components/legal/Disclaimer";
import AnalyticsDashboard from "./components/admin/AnalyticsDashboard";
import LandingPage from "./components/landing/LandingPage";
import VerifyEmailBanner from "./components/auth/VerifyEmailBanner";
import VerifyEmail from "./components/auth/VerifyEmail";
import Settings from "./components/Settings";
import HelpCenter from "./components/help/HelpCenter";

// Import analytics
import { trackPageView } from "./utils/analytics";

// Import context
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import ToastProvider from "./components/ui/Toast";

const PageViewTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => { trackPageView(pathname); }, [pathname]);
  return null;
};

const KeyboardShortcuts = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const handleKey = (e) => {
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (!e.shiftKey) return;
      switch (e.key.toUpperCase()) {
        case 'J': navigate('/journal');   break;
        case 'A': navigate('/analytics'); break;
        case 'B': navigate('/brokers');   break;
        default:  break;
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [navigate]);
  return null;
};

// Main App Component
const App = () => {
  const { user, isLoading } = useAuth();

  useEffect(() => {
    const handleMessage = (event) => {
      console.log("App.js: Message received:", event.data, "from origin:", event.origin);
      if (event.origin !== window.location.origin) return;
      if (event.data.type === "SCHWAB_OAUTH_SUCCESS" || event.data.type === "BROKER_OAUTH_SUCCESS") {
        window.location.href = "/brokers";
      } else if (event.data.type === "SCHWAB_OAUTH_ERROR" || event.data.type === "BROKER_OAUTH_ERROR") {
        console.error(`Authorization failed: ${event.data.error}`);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-bounce">📈</div>
          <p className="text-slate-400 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <PageViewTracker />
      <KeyboardShortcuts />

      {user ? (
        // Authenticated: /landing renders without app shell; everything else inside Layout
        <>
        <VerifyEmailBanner />
        <Routes>
          <Route
            path="/landing"
            element={<ErrorBoundary><LandingPage /></ErrorBoundary>}
          />
          <Route
            path="*"
            element={
              <Layout>
                <Routes>
                  <Route path="/"                   element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
                  <Route path="/trades"             element={<ErrorBoundary><Trades /></ErrorBoundary>} />
                  <Route path="/positions"          element={<ErrorBoundary><Positions /></ErrorBoundary>} />
                  <Route path="/journal"            element={<ErrorBoundary><Journal /></ErrorBoundary>} />
                  <Route path="/playbooks"          element={<ErrorBoundary><Playbooks /></ErrorBoundary>} />
                  <Route path="/brokers"            element={<ErrorBoundary><Brokers /></ErrorBoundary>} />
                  <Route path="/analytics"          element={<ErrorBoundary><Analytics /></ErrorBoundary>} />
                  <Route path="/option-lab"         element={<ErrorBoundary><OptionAnalytics /></ErrorBoundary>} />
                  <Route path="/tools/position-sizer" element={<ErrorBoundary><PositionSizer /></ErrorBoundary>} />
                  <Route path="/tools/import"       element={<ErrorBoundary><ImportWizard /></ErrorBoundary>} />
                  <Route path="/strategy"           element={<ErrorBoundary><Strategy /></ErrorBoundary>} />
                  <Route path="/oauth/callback"     element={<ErrorBoundary><OAuthCallback /></ErrorBoundary>} />
                  <Route path="/privacy"            element={<ErrorBoundary><PrivacyPolicy /></ErrorBoundary>} />
                  <Route path="/terms"              element={<ErrorBoundary><TermsOfService /></ErrorBoundary>} />
                  <Route path="/disclaimer"         element={<ErrorBoundary><Disclaimer /></ErrorBoundary>} />
                  <Route path="/admin/analytics"    element={<ErrorBoundary><AnalyticsDashboard /></ErrorBoundary>} />
                  <Route path="/settings"           element={<ErrorBoundary><Settings /></ErrorBoundary>} />
                  <Route path="/help"               element={<ErrorBoundary><HelpCenter /></ErrorBoundary>} />
                  <Route path="/login"              element={<Navigate to="/" />} />
                  <Route path="*"                   element={<Navigate to="/" />} />
                </Routes>
              </Layout>
            }
          />
        </Routes>
        </>
      ) : (
        // Unauthenticated: landing page at /, login form at /login
        <Routes>
          <Route path="/"              element={<ErrorBoundary><LandingPage /></ErrorBoundary>} />
          <Route path="/login"         element={<ErrorBoundary><LoginForm /></ErrorBoundary>} />
          <Route path="/reset-password" element={<ErrorBoundary><ResetPassword /></ErrorBoundary>} />
          <Route path="/verify-email"  element={<ErrorBoundary><VerifyEmail /></ErrorBoundary>} />
          <Route path="/privacy"       element={<ErrorBoundary><PrivacyPolicy /></ErrorBoundary>} />
          <Route path="/terms"         element={<ErrorBoundary><TermsOfService /></ErrorBoundary>} />
          <Route path="/disclaimer"    element={<ErrorBoundary><Disclaimer /></ErrorBoundary>} />
          <Route path="*"              element={<Navigate to="/" />} />
        </Routes>
      )}
    </Router>
  );
};

// Root Component with Context
const TradingJournalApp = () => {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || "dummy_id"}>
      <HelmetProvider>
        <ToastProvider>
          <AuthProvider>
            <ThemeProvider>
              <App />
            </ThemeProvider>
          </AuthProvider>
        </ToastProvider>
      </HelmetProvider>
    </GoogleOAuthProvider>
  );
};

export default TradingJournalApp;
